import React, { useMemo } from "react";
import {
  type GameState,
  type HistoryEntry,
  type Player,
  type PlayerKey,
} from "../vowGameLogic";
import { PANEL_CLASS } from "./uiConstants";
import { SCORE_METADATA } from "./scoreMetadata";

const createTint = (hex: string, alpha: number) => {
  if (!hex.startsWith("#")) {
    return `rgba(0, 0, 0, ${alpha})`;
  }

  const normalized =
    hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex;

  if (normalized.length !== 7) {
    return `rgba(0, 0, 0, ${alpha})`;
  }

  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

type PlayerDashboardCardProps = {
  playerKey: PlayerKey;
  player: Player;
  lastHistory?: HistoryEntry;
  previousHistory?: HistoryEntry;
};

export const PlayerDashboardCard: React.FC<PlayerDashboardCardProps> = ({
  playerKey,
  player,
  lastHistory,
  previousHistory,
}) => {
  const accentTint = useMemo(
    () => createTint(player.color, 0.28),
    [player.color]
  );
  const accentBorder = useMemo(
    () => createTint(player.color, 0.48),
    [player.color]
  );

  const gainDisplay = useMemo(() => {
    if (!lastHistory) {
      return "";
    }
    const prevScore = previousHistory?.players[playerKey]?.score ?? 0;
    const gain = player.score - prevScore;
    const label = previousHistory ? "Act Gain" : "Initial Score";
    const sign = gain >= 0 ? "+" : "";
    return `${label}: ${sign}${Math.round(gain)}`;
  }, [lastHistory, player.score, playerKey, previousHistory]);

  const scoreSummary = useMemo(() => {
    if (!lastHistory) {
      return "";
    }
    const { scores, habitatCounts } = lastHistory;
    switch (playerKey) {
      case "herder":
        return `Meadows: ${habitatCounts.P} tiles`;
      case "reed_harvester":
        return `Reedbeds: ${habitatCounts.R} tiles`;
      case "hunter":
        return `${SCORE_METADATA.D.label}: ${Math.round(scores.D)}`;
      case "protector":
        return `${SCORE_METADATA.BIO.label}: ${Math.round(scores.BIO)} | ${SCORE_METADATA.BHQ.label}: ${Math.round(scores.BHQ)}`;
      case "bittern":
        return `${SCORE_METADATA.Births.label}: ${Math.round(
          scores.Births
        )} | ${SCORE_METADATA.BHQ.label}: ${Math.round(scores.BHQ)}`;
      default:
        return "";
    }
  }, [lastHistory, playerKey]);

  return (
    <div
      className="flex h-full flex-col justify-between rounded-xl border-2 bg-[#BCB7A8] p-4 text-slate-900 shadow-sm"
      style={{ borderColor: accentBorder}}
    >
      <div>
        <span
          className="inline-flex rounded-lg px-2 py-1 text-sm font-semibold max-w-full"
          style={{
            backgroundColor: accentTint,
          }}
        >
          {player.name}
        </span>
        <p className="mt-2 text-xs italic text-slate-700">{player.objective}</p>
      </div>
      <div className="mt-3 space-y-1 text-right">
        {scoreSummary && <p className="text-xs text-slate-700">{scoreSummary}</p>}
        {gainDisplay && (
          <p
            className={`text-sm font-semibold ${
              gainDisplay.includes("-") ? "text-rose-600" : "text-emerald-600"
            }`}
          >
            {gainDisplay}
          </p>
        )}
        <p className="rounded-lg bg-white/70 px-2 py-1 text-2xl font-black text-slate-900 shadow-inner">
          {Math.round(player.score)}
        </p>
      </div>
    </div>
  );
};

type PlayerDashboardsSectionProps = {
  gameState: GameState;
  lastHistory?: HistoryEntry;
  previousHistory?: HistoryEntry;
};

const PlayerDashboardsSection: React.FC<PlayerDashboardsSectionProps> = ({
  gameState,
  lastHistory,
  previousHistory,
}) => (
  <section
    className={`${PANEL_CLASS} border-[#5f5642] bg-[#a89f84] p-6 shadow-md`}
  >
    <h2 className="text-center text-2xl font-bold text-slate-800">
      Player Dashboards
    </h2>
    <div
      id="player-dashboards"
      className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5"
    >
      {(Object.entries(gameState.players) as Array<[PlayerKey, Player]>).map(
        ([playerKey, player]) => (
          <PlayerDashboardCard
            key={playerKey}
            playerKey={playerKey}
            player={player}
            lastHistory={lastHistory}
            previousHistory={previousHistory}
          />
        )
      )}
    </div>
  </section>
);

export default PlayerDashboardsSection;
